import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { GripVertical, ImageIcon, Pencil, Trash2, X } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { useAdminServices } from '../hooks/useAdminServices'
import { useAdminServiceCategories } from '../hooks/useAdminServiceCategories'
import { useAdminAddons } from '../hooks/useAdminAddons'
import { useAdminPositions } from '../hooks/useAdminPositions'
import { ServicePhotoSheet } from '../components/services/ServicePhotoSheet'
import type { ServiceDocument, ServiceCategoryDocument, CatalogAddonDocument, PricingType, SelectionMode, AddonPricingMode } from '@/types'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toSlug(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

const PRICING_TYPES: { value: PricingType; label: string }[] = [
  { value: 'flat',        label: 'Flat rate'           },
  { value: 'hourly',      label: 'Hourly (per hour)'   },
  { value: 'perMinute',   label: 'Per minute'          },
  { value: 'perHeadshot', label: 'Per headshot'        },
  { value: 'starting_at', label: 'Starting at'         },
  { value: 'range',       label: 'Price range'         },
  { value: 'contact',     label: 'Contact for pricing' },
]

const SELECTION_MODES: { value: SelectionMode; label: string }[] = [
  { value: 'boolean', label: 'Toggle (yes/no)'    },
  { value: 'integer', label: 'Quantity (integer)' },
  { value: 'string',  label: 'Custom text'        },
]

const ADDON_PRICING_MODES: { value: AddonPricingMode; label: string }[] = [
  { value: 'flat',      label: 'Flat rate'  },
  { value: 'perHour',   label: 'Per hour'   },
  { value: 'perMinute', label: 'Per minute' },
]

function formatPricingLabel(pricingType: PricingType, price?: number, priceMax?: number): string {
  switch (pricingType) {
    case 'flat':        return price !== undefined ? `$${price}` : '—'
    case 'hourly':      return price !== undefined ? `$${price}/hr` : '—'
    case 'perMinute':   return price !== undefined ? `$${price}/min` : '—'
    case 'perHeadshot': return price !== undefined ? `$${price}/headshot` : '—'
    case 'starting_at': return price !== undefined ? `Starting at $${price}` : '—'
    case 'range':       return price !== undefined && priceMax !== undefined ? `$${price}–$${priceMax}` : '—'
    case 'contact':     return 'Contact for pricing'
  }
}

// ─── Service form schema ───────────────────────────────────────────────────────

const serviceSchema = z
  .object({
    title:       z.string().min(1, 'Title is required'),
    description: z.string().min(1, 'Description is required'),
    category:    z.string().min(1, 'Category is required'),
    pricingType: z.enum(['flat', 'hourly', 'perMinute', 'perHeadshot', 'starting_at', 'range', 'contact']),
    price:       z.union([z.number().min(0), z.literal('')]),
    priceMax:    z.union([z.number().min(0), z.literal('')]),
    pricingNote: z.string(),
    ctaLabel:    z.string(),
    ctaUrl:      z.string(),
    isPublished: z.boolean(),
    order:       z.number().int().min(0),
    // Hidden catalog fields — auto-derived, not shown in UI
    serviceKey:               z.string(),
    isEvent:                  z.boolean(),
    hasComplexity:            z.boolean(),
    complexitySimplePrice:    z.union([z.number().min(0), z.literal('')]),
    complexityModeratePrice:  z.union([z.number().min(0), z.literal('')]),
    complexityComplexPrice:   z.union([z.number().min(0), z.literal('')]),
    complexitySimpleText:     z.string(),
    complexityModerateText:   z.string(),
    complexityComplexText:    z.string(),
    setupTime:                z.union([z.number().min(0), z.literal('')]),
    showAudioOption:          z.boolean(),
    addonIds:                 z.array(z.string()),
    providerPositionIds:      z.array(z.string()),
    // Photo fields — managed via ServicePhotoSheet, not HTML inputs
    photoMediaId: z.string(),
    photoUrl:     z.string(),
    photoFocusX:  z.number(),
    photoFocusY:  z.number(),
  })
  .superRefine((data, ctx) => {
    if (data.pricingType !== 'contact' && data.price === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Price is required for this pricing type', path: ['price'] })
    }
    if (data.pricingType === 'range' && data.priceMax === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Max price is required for a price range', path: ['priceMax'] })
    }
  })

type ServiceFormSchema = z.infer<typeof serviceSchema>

const SERVICE_DEFAULTS: ServiceFormSchema = {
  title: '', description: '', category: '',
  pricingType: 'flat', price: '', priceMax: '',
  pricingNote: '', ctaLabel: '', ctaUrl: '',
  isPublished: false, order: 0,
  serviceKey: '',
  isEvent: false, hasComplexity: false,
  complexitySimplePrice: '', complexityModeratePrice: '', complexityComplexPrice: '',
  complexitySimpleText: '', complexityModerateText: '', complexityComplexText: '',
  setupTime: '', showAudioOption: false,
  addonIds: [],
  providerPositionIds: [],
  photoMediaId: '', photoUrl: '', photoFocusX: 0.5, photoFocusY: 0.5,
}

// ─── Addon form schema ─────────────────────────────────────────────────────────

const addonSchema = z
  .object({
    label:         z.string().min(1, 'Label is required'),
    pricingType:   z.enum(['flat', 'hourly', 'perMinute', 'perHeadshot', 'starting_at', 'range', 'contact']),
    price:         z.union([z.number().min(0), z.literal('')]),
    priceMax:      z.union([z.number().min(0), z.literal('')]),
    pricingNote:   z.string(),
    selectionMode: z.enum(['boolean', 'integer', 'string']),
    pricingMode:   z.enum(['flat', 'perHour', 'perMinute']),
    rate:          z.union([z.number().min(0), z.literal('')]),
    maxQty:        z.union([z.number().int().min(1), z.literal('')]),
    isPublished:   z.boolean(),
    order:         z.number().int().min(0),
  })
  .superRefine((data, ctx) => {
    if (data.pricingType !== 'contact' && data.price === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Price is required', path: ['price'] })
    }
    if (data.pricingType === 'range' && data.priceMax === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Max price is required', path: ['priceMax'] })
    }
  })

type AddonFormSchema = z.infer<typeof addonSchema>

const ADDON_DEFAULTS: AddonFormSchema = {
  label: '', pricingType: 'flat', price: '', priceMax: '', pricingNote: '',
  selectionMode: 'boolean', pricingMode: 'flat', rate: '', maxQty: '',
  isPublished: false, order: 0,
}

// ─── Sortable row wrapper ─────────────────────────────────────────────────────

function SortableRow({ id, children }: { id: string; children: (handle: React.ReactNode) => React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const handle = (
    <button
      type="button"
      className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground"
      {...attributes}
      {...listeners}
    >
      <GripVertical className="h-4 w-4" />
    </button>
  )
  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {children(handle)}
    </TableRow>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AdminServicesPage() {
  const {
    services,
    loading: svcLoading,
    error: svcError,
    createService,
    updateService,
    deleteService,
  } = useAdminServices()

  const {
    categories,
    loading: catLoading,
    error: catError,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useAdminServiceCategories()

  // ── Service photo sheet ──────────────────────────────────────────────────────
  const [svcPhotoSheetOpen, setSvcPhotoSheetOpen] = useState(false)

  // ── Service form dialog ──────────────────────────────────────────────────────
  const [svcFormOpen, setSvcFormOpen] = useState(false)
  const [svcEditTarget, setSvcEditTarget] = useState<(ServiceDocument & { id: string }) | null>(null)
  const [svcSubmitError, setSvcSubmitError] = useState<string | null>(null)
  const [currentSlug, setCurrentSlug] = useState('')

  const {
    register,
    handleSubmit,
    reset: resetSvcForm,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ServiceFormSchema>({
    resolver: zodResolver(serviceSchema),
    defaultValues: SERVICE_DEFAULTS,
  })

  const watchedTitle = watch('title')
  const watchedCategory = watch('category')
  const watchedPricingType = watch('pricingType')
  const watchedHasComplexity = watch('hasComplexity')
  const showPriceField = watchedPricingType !== 'contact'
  const showPriceMaxField = watchedPricingType === 'range'
  const isBookablePricingType = ['flat', 'hourly', 'perMinute', 'perHeadshot'].includes(watchedPricingType)

  useEffect(() => {
    setCurrentSlug(toSlug(watchedTitle))
  }, [watchedTitle])

  // Auto-derive serviceKey from selected category
  useEffect(() => {
    const cat = categories.find((c) => c.title === watchedCategory)
    setValue('serviceKey', cat?.serviceKey ?? '')
  }, [watchedCategory, categories, setValue])

  // Show setup time only for hourly
  const showSetupTime = watchedPricingType === 'hourly'

  function openSvcAddForm() {
    setSvcEditTarget(null)
    setSvcSubmitError(null)
    resetSvcForm(SERVICE_DEFAULTS)
    setSvcFormOpen(true)
  }

  function openSvcEditForm(service: ServiceDocument & { id: string }) {
    setSvcEditTarget(service)
    setSvcSubmitError(null)
    resetSvcForm({
      title:       service.title,
      description: service.description,
      category:    service.category,
      pricingType: service.pricingType,
      price:       service.price ?? '',
      priceMax:    service.priceMax ?? '',
      pricingNote: service.pricingNote ?? '',
      ctaLabel:    service.ctaLabel ?? '',
      ctaUrl:      service.ctaUrl ?? '',
      isPublished: service.isPublished,
      order:       service.order,
      serviceKey:              service.serviceKey ?? '',
      isEvent:                 service.isEvent ?? false,
      hasComplexity:           service.hasComplexity ?? false,
      complexitySimplePrice:   service.complexityBase?.simple ?? '',
      complexityModeratePrice: service.complexityBase?.moderate ?? '',
      complexityComplexPrice:  service.complexityBase?.complex ?? '',
      complexitySimpleText:    service.complexityTexts?.['simple'] ?? '',
      complexityModerateText:  service.complexityTexts?.['moderate'] ?? '',
      complexityComplexText:   service.complexityTexts?.['complex'] ?? '',
      setupTime:               service.setupTime ?? '',
      showAudioOption:         service.showAudioOption ?? false,
      addonIds:                service.addonIds ?? [],
      providerPositionIds:     service.providerPositionIds ?? [],
      photoMediaId: service.photoMediaId ?? '',
      photoUrl:     service.photoUrl ?? '',
      photoFocusX:  service.photoFocusX ?? 0.5,
      photoFocusY:  service.photoFocusY ?? 0.5,
    })
    setCurrentSlug(service.slug)
    setSvcFormOpen(true)
  }

  async function onSvcSubmit(values: ServiceFormSchema) {
    setSvcSubmitError(null)
    try {
      if (svcEditTarget) {
        await updateService(svcEditTarget.id, values)
      } else {
        await createService(values)
      }
      setSvcFormOpen(false)
    } catch (err) {
      setSvcSubmitError(formatUserFacingError(err, 'Failed to save service'))
    }
  }

  // ── Service delete dialog ────────────────────────────────────────────────────
  const [svcDeleteOpen, setSvcDeleteOpen] = useState(false)
  const [svcDeleteTarget, setSvcDeleteTarget] = useState<(ServiceDocument & { id: string }) | null>(null)
  const [svcDeletingId, setSvcDeletingId] = useState<string | null>(null)

  async function handleSvcDelete() {
    if (!svcDeleteTarget) return
    setSvcDeletingId(svcDeleteTarget.id)
    try {
      await deleteService(svcDeleteTarget.id)
      setSvcDeleteOpen(false)
      setSvcDeleteTarget(null)
    } finally {
      setSvcDeletingId(null)
    }
  }

  // ── Category form dialog ─────────────────────────────────────────────────────
  const [catFormOpen, setCatFormOpen] = useState(false)
  const [catEditTarget, setCatEditTarget] = useState<(ServiceCategoryDocument & { id: string }) | null>(null)
  const [catTitleInput, setCatTitleInput] = useState('')
  const [catKeyInput, setCatKeyInput] = useState('')
  const [catSaving, setCatSaving] = useState(false)

  function openCatAddForm() {
    setCatEditTarget(null)
    setCatTitleInput('')
    setCatKeyInput('')
    setCatFormOpen(true)
  }

  function openCatEditForm(cat: ServiceCategoryDocument & { id: string }) {
    setCatEditTarget(cat)
    setCatTitleInput(cat.title)
    setCatKeyInput(cat.serviceKey ?? '')
    setCatFormOpen(true)
  }

  async function handleCatSave() {
    const trimmed = catTitleInput.trim()
    if (!trimmed) return
    setCatSaving(true)
    try {
      if (catEditTarget) {
        await updateCategory(catEditTarget.id, {
          title: trimmed,
          serviceKey: catKeyInput.trim() || trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, ''),
        })
      } else {
        await createCategory(trimmed, categories.length + 1, catKeyInput.trim() || undefined)
      }
      setCatFormOpen(false)
    } finally {
      setCatSaving(false)
    }
  }

  // ── Category delete dialog ───────────────────────────────────────────────────
  const [catDeleteOpen, setCatDeleteOpen] = useState(false)
  const [catDeleteTarget, setCatDeleteTarget] = useState<(ServiceCategoryDocument & { id: string }) | null>(null)
  const [catDeleting, setCatDeleting] = useState(false)

  async function handleCatDelete() {
    if (!catDeleteTarget) return
    setCatDeleting(true)
    try {
      await deleteCategory(catDeleteTarget.id)
      setCatDeleteOpen(false)
      setCatDeleteTarget(null)
    } finally {
      setCatDeleting(false)
    }
  }

  // ── Add-ons ──────────────────────────────────────────────────────────────────
  const {
    addons,
    loading: addonLoading,
    error: addonError,
    createAddon,
    updateAddon,
    deleteAddon,
  } = useAdminAddons()

  const { positions } = useAdminPositions()
  const activePositions = positions.filter((p) => p.isActive)

  // ── Drag & drop ordering ────────────────────────────────────────────────────
  const [orderedServices, setOrderedServices] = useState<Array<ServiceDocument & { id: string }>>([])
  const [orderedCategories, setOrderedCategories] = useState<Array<ServiceCategoryDocument & { id: string }>>([])
  const [orderedAddons, setOrderedAddons] = useState<Array<CatalogAddonDocument & { id: string }>>([])
  const [savingOrder, setSavingOrder] = useState(false)
  const [orderError, setOrderError] = useState<string | null>(null)

  useEffect(() => {
    setOrderedServices([...services].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
  }, [services])
  useEffect(() => {
    setOrderedCategories([...categories].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
  }, [categories])
  useEffect(() => {
    setOrderedAddons([...addons].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
  }, [addons])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  async function persistOrder<T extends { id: string }>(
    items: T[],
    update: (id: string, data: { order: number }) => Promise<void>,
  ) {
    setSavingOrder(true)
    setOrderError(null)
    try {
      await Promise.all(items.map((item, i) => update(item.id, { order: i + 1 })))
    } catch (err) {
      setOrderError(formatUserFacingError(err, 'Failed to save order'))
    } finally {
      setSavingOrder(false)
    }
  }

  function handleServicesDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = orderedServices.findIndex((s) => s.id === active.id)
    const newIndex = orderedServices.findIndex((s) => s.id === over.id)
    const next = arrayMove(orderedServices, oldIndex, newIndex)
    setOrderedServices(next)
    void persistOrder(next, (id, data) => updateService(id, data))
  }

  function handleCategoriesDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = orderedCategories.findIndex((c) => c.id === active.id)
    const newIndex = orderedCategories.findIndex((c) => c.id === over.id)
    const next = arrayMove(orderedCategories, oldIndex, newIndex)
    setOrderedCategories(next)
    void persistOrder(next, (id, data) => updateCategory(id, data))
  }

  function handleAddonsDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = orderedAddons.findIndex((a) => a.id === active.id)
    const newIndex = orderedAddons.findIndex((a) => a.id === over.id)
    const next = arrayMove(orderedAddons, oldIndex, newIndex)
    setOrderedAddons(next)
    void persistOrder(next, (id, data) => updateAddon(id, data))
  }

  // ── Addon form dialog ────────────────────────────────────────────────────────
  const [addonFormOpen, setAddonFormOpen] = useState(false)
  const [addonEditTarget, setAddonEditTarget] = useState<(CatalogAddonDocument & { id: string }) | null>(null)
  const [addonSubmitError, setAddonSubmitError] = useState<string | null>(null)

  const {
    register: registerAddon,
    handleSubmit: handleAddonSubmit,
    reset: resetAddonForm,
    watch: watchAddon,
    control: addonControl,
    formState: { errors: addonErrors, isSubmitting: addonIsSubmitting },
  } = useForm<AddonFormSchema>({
    resolver: zodResolver(addonSchema),
    defaultValues: ADDON_DEFAULTS,
  })

  const watchedAddonPricingType = watchAddon('pricingType')
  const watchedAddonSelectionMode = watchAddon('selectionMode')
  const showAddonPriceField    = watchedAddonPricingType !== 'contact'
  const showAddonPriceMaxField = watchedAddonPricingType === 'range'
  const showAddonMaxQty        = watchedAddonSelectionMode === 'integer'

  function openAddonAddForm() {
    setAddonEditTarget(null)
    setAddonSubmitError(null)
    resetAddonForm(ADDON_DEFAULTS)
    setAddonFormOpen(true)
  }

  function openAddonEditForm(addon: CatalogAddonDocument & { id: string }) {
    setAddonEditTarget(addon)
    setAddonSubmitError(null)
    resetAddonForm({
      label:         addon.label,
      pricingType:   addon.pricingType ?? 'flat',
      price:         addon.price ?? '',
      priceMax:      addon.priceMax ?? '',
      pricingNote:   addon.pricingNote ?? '',
      selectionMode: addon.selectionMode,
      pricingMode:   addon.pricingMode,
      rate:          addon.rate ?? '',
      maxQty:        addon.maxQty ?? '',
      isPublished:   addon.isPublished ?? false,
      order:         addon.order ?? 0,
    })
    setAddonFormOpen(true)
  }

  async function onAddonSubmit(values: AddonFormSchema) {
    setAddonSubmitError(null)
    try {
      if (addonEditTarget) {
        await updateAddon(addonEditTarget.id, values)
      } else {
        await createAddon(values)
      }
      setAddonFormOpen(false)
    } catch (err) {
      setAddonSubmitError(formatUserFacingError(err, 'Failed to save add-on'))
    }
  }

  // ── Addon delete dialog ──────────────────────────────────────────────────────
  const [addonDeleteOpen, setAddonDeleteOpen] = useState(false)
  const [addonDeleteTarget, setAddonDeleteTarget] = useState<(CatalogAddonDocument & { id: string }) | null>(null)
  const [addonDeletingId, setAddonDeletingId] = useState<string | null>(null)

  async function handleAddonDelete() {
    if (!addonDeleteTarget) return
    setAddonDeletingId(addonDeleteTarget.id)
    try {
      await deleteAddon(addonDeleteTarget.id)
      setAddonDeleteOpen(false)
      setAddonDeleteTarget(null)
    } finally {
      setAddonDeletingId(null)
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8">

      {savingOrder && <p className="text-xs text-muted-foreground">Saving order…</p>}
      {orderError && <p className="text-sm text-destructive">{orderError}</p>}

      {/* ─── Services ─────────────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold">Services</h2>
            <p className="text-sm text-muted-foreground">
              Manage the service offerings displayed on the public services page.
            </p>
          </div>
          <Button onClick={openSvcAddForm}>Add Service</Button>
        </div>

        {svcError && <p className="text-sm text-destructive">{svcError}</p>}

        {svcLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <div className="rounded-md border">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleServicesDragEnd}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Pricing</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead>Request Form</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <MultiButtonGroup>
                <SortableContext items={orderedServices.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                <TableBody>
                  {orderedServices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                        No services yet. Add one to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    orderedServices.map((service) => {
                      const isBookablePricing = ['flat', 'hourly', 'perMinute', 'perHeadshot'].includes(service.pricingType)
                      const hasServiceKey = !!service.serviceKey
                      const appearsInForm = service.isPublished && isBookablePricing && hasServiceKey
                      return (
                      <SortableRow key={service.id} id={service.id}>{(handle) => (<>
                        <TableCell className="w-8 pr-0">{handle}</TableCell>
                        <TableCell className="font-medium">{service.title}</TableCell>
                        <TableCell>{service.category}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatPricingLabel(service.pricingType, service.price, service.priceMax)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={service.isPublished ? 'default' : 'secondary'}>
                            {service.isPublished ? 'Published' : 'Draft'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={appearsInForm ? 'default' : 'outline'} className={appearsInForm ? '' : 'text-muted-foreground'}>
                            {appearsInForm ? 'Visible' : !hasServiceKey ? 'No category key' : !isBookablePricing ? 'Non-bookable pricing' : 'Draft'}
                          </Badge>
                        </TableCell>
                        <TableCell>{service.order}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end">
                            <MultiButton
                              variant="outline"
                              size="sm"
                              items={[
                                { id: 'edit',     icon: Pencil,    label: 'Edit',          onClick: () => openSvcEditForm(service) },
                                { id: 'delete',   icon: Trash2,    label: 'Delete',        onClick: () => { setSvcDeleteTarget(service); setSvcDeleteOpen(true) }, disabled: svcDeletingId === service.id, className: 'text-destructive hover:text-destructive' },
                              ]}
                            />
                          </div>
                        </TableCell>
                      </>)}</SortableRow>
                      )
                    })
                  )}
                </TableBody>
                </SortableContext>
              </MultiButtonGroup>
            </Table>
            </DndContext>
          </div>
        )}
      </section>

      <Separator />

      {/* ─── Categories ───────────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold">Categories</h2>
            <p className="text-sm text-muted-foreground">
              Define the categories used to group services.
            </p>
          </div>
          <Button onClick={openCatAddForm}>Add Category</Button>
        </div>

        {catError && <p className="text-sm text-destructive">{catError}</p>}

        {catLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <div className="rounded-md border">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoriesDragEnd}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Title</TableHead>
                  <TableHead>Service Key</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <MultiButtonGroup>
                <SortableContext items={orderedCategories.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                <TableBody>
                  {orderedCategories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                        No categories yet. Add one to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    orderedCategories.map((cat) => (
                      <SortableRow key={cat.id} id={cat.id}>{(handle) => (<>
                        <TableCell className="w-8 pr-0">{handle}</TableCell>
                        <TableCell className="font-medium">{cat.title}</TableCell>
                        <TableCell className="font-mono text-sm text-muted-foreground">{cat.serviceKey}</TableCell>
                        <TableCell>{cat.order}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end">
                            <MultiButton
                              variant="outline"
                              size="sm"
                              items={[
                                { id: 'edit',   icon: Pencil, label: 'Edit',   onClick: () => openCatEditForm(cat) },
                                { id: 'delete', icon: Trash2, label: 'Delete', onClick: () => { setCatDeleteTarget(cat); setCatDeleteOpen(true) }, className: 'text-destructive hover:text-destructive' },
                              ]}
                            />
                          </div>
                        </TableCell>
                      </>)}</SortableRow>
                    ))
                  )}
                </TableBody>
                </SortableContext>
              </MultiButtonGroup>
            </Table>
            </DndContext>
          </div>
        )}
      </section>

      <Separator />

      {/* ─── Add-ons ──────────────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold">Add-ons</h2>
            <p className="text-sm text-muted-foreground">
              Manage optional add-ons that can be attached to services.
            </p>
          </div>
          <Button onClick={openAddonAddForm}>Add Add-on</Button>
        </div>

        {addonError && <p className="text-sm text-destructive">{addonError}</p>}

        {addonLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <div className="rounded-md border">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleAddonsDragEnd}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Label</TableHead>
                  <TableHead>Pricing</TableHead>
                  <TableHead>Selection</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <MultiButtonGroup>
                <SortableContext items={orderedAddons.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                <TableBody>
                  {orderedAddons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                        No add-ons yet. Add one to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    orderedAddons.map((addon) => (
                      <SortableRow key={addon.id} id={addon.id}>{(handle) => (<>
                        <TableCell className="w-8 pr-0">{handle}</TableCell>
                        <TableCell className="font-medium">{addon.label}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {addon.pricingType
                            ? formatPricingLabel(addon.pricingType, addon.price, addon.priceMax)
                            : '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground capitalize">{addon.selectionMode}</TableCell>
                        <TableCell>
                          <Badge variant={addon.isPublished ? 'default' : 'secondary'}>
                            {addon.isPublished ? 'Published' : 'Draft'}
                          </Badge>
                        </TableCell>
                        <TableCell>{addon.order}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end">
                            <MultiButton
                              variant="outline"
                              size="sm"
                              items={[
                                { id: 'edit',     icon: Pencil,    label: 'Edit',          onClick: () => openAddonEditForm(addon) },
                                { id: 'delete',   icon: Trash2,    label: 'Delete',        onClick: () => { setAddonDeleteTarget(addon); setAddonDeleteOpen(true) }, disabled: addonDeletingId === addon.id, className: 'text-destructive hover:text-destructive' },
                              ]}
                            />
                          </div>
                        </TableCell>
                      </>)}</SortableRow>
                    ))
                  )}
                </TableBody>
                </SortableContext>
              </MultiButtonGroup>
            </Table>
            </DndContext>
          </div>
        )}
      </section>

      {/* ─── Service form dialog ──────────────────────────────────────────────── */}
      <Dialog open={svcFormOpen} onOpenChange={setSvcFormOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{svcEditTarget ? 'Edit Service' : 'New Service'}</DialogTitle>
          </DialogHeader>

          <form
            id="service-form"
            onSubmit={(e) => void handleSubmit(onSvcSubmit)(e)}
            className="space-y-4 py-1"
          >
            {/* Title */}
            <div className="space-y-1">
              <Label htmlFor="svc-title">Title</Label>
              <Input id="svc-title" placeholder="e.g. Event Photography" {...register('title')} />
              {watchedTitle && (
                <p className="text-xs text-muted-foreground">Slug: {currentSlug}</p>
              )}
              {errors.title && <p className="text-sm text-destructive">{withErrorCode(String(errors.title.message), 'app/validation')}</p>}
            </div>

            {/* Description */}
            <div className="space-y-1">
              <Label htmlFor="svc-description">Description</Label>
              <Textarea id="svc-description" rows={3} placeholder="Describe this service…" {...register('description')} />
              {errors.description && <p className="text-sm text-destructive">{withErrorCode(String(errors.description.message), 'app/validation')}</p>}
            </div>

            {/* Category */}
            <div className="space-y-1">
              <Label>Category</Label>
              {categories.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No categories configured. Add one in the Categories section below.
                </p>
              ) : (
                <Controller
                  control={control}
                  name="category"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a category…" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.title}>{cat.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              )}
              {errors.category && <p className="text-sm text-destructive">{withErrorCode(String(errors.category.message), 'app/validation')}</p>}
            </div>

            {/* Pricing type */}
            <div className="space-y-1">
              <Label>Pricing Type</Label>
              <Controller
                control={control}
                name="pricingType"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRICING_TYPES.map(({ value, label }) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Price fields */}
            {showPriceField && (
              <div className={showPriceMaxField ? 'grid grid-cols-2 gap-4' : undefined}>
                <div className="space-y-1">
                  <Label htmlFor="svc-price">
                    {watchedPricingType === 'range' ? 'Min Price ($)' : 'Price ($)'}
                  </Label>
                  <Input
                    id="svc-price"
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder="0"
                    {...register('price', { valueAsNumber: true })}
                  />
                  {errors.price && <p className="text-sm text-destructive">{withErrorCode(String(errors.price.message), 'app/validation')}</p>}
                </div>
                {showPriceMaxField && (
                  <div className="space-y-1">
                    <Label htmlFor="svc-price-max">Max Price ($)</Label>
                    <Input
                      id="svc-price-max"
                      type="number"
                      min={0}
                      step={0.01}
                      placeholder="0"
                      {...register('priceMax', { valueAsNumber: true })}
                    />
                    {errors.priceMax && <p className="text-sm text-destructive">{withErrorCode(String(errors.priceMax.message), 'app/validation')}</p>}
                  </div>
                )}
              </div>
            )}

            {/* Pricing note */}
            <div className="space-y-1">
              <Label htmlFor="svc-pricing-note">
                Pricing Note <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input id="svc-pricing-note" placeholder="e.g. per session, minimum 2 hours" {...register('pricingNote')} />
            </div>

            {/* CTA */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="svc-cta-label">
                  CTA Label <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Input id="svc-cta-label" placeholder="e.g. Book Now" {...register('ctaLabel')} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="svc-cta-url">
                  CTA URL <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Input id="svc-cta-url" placeholder="https://…" {...register('ctaUrl')} />
              </div>
            </div>

            {/* Photo */}
            <div className="space-y-2">
              <Label>
                Photo <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              {watch('photoUrl') ? (
                <div className="space-y-2">
                  <div
                    className="relative overflow-hidden rounded-lg border"
                    style={{ aspectRatio: '16/9' }}
                  >
                    <img
                      src={watch('photoUrl')}
                      alt="Service photo"
                      className="h-full w-full object-cover"
                      style={{
                        objectPosition: `${watch('photoFocusX') * 100}% ${watch('photoFocusY') * 100}%`,
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSvcPhotoSheetOpen(true)}
                      className="gap-1.5"
                    >
                      <ImageIcon className="h-3.5 w-3.5" />
                      Change Photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setValue('photoMediaId', '')
                        setValue('photoUrl', '')
                        setValue('photoFocusX', 0.5)
                        setValue('photoFocusY', 0.5)
                      }}
                      className="gap-1.5"
                    >
                      <X className="h-3.5 w-3.5" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSvcPhotoSheetOpen(true)}
                  className="gap-1.5"
                >
                  <ImageIcon className="h-3.5 w-3.5" />
                  Select Photo
                </Button>
              )}
            </div>

            {/* Order + Published */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="svc-order">Sort Order</Label>
                <Input id="svc-order" type="number" min={0} {...register('order', { valueAsNumber: true })} />
                {errors.order && <p className="text-sm text-destructive">{withErrorCode(String(errors.order.message), 'app/validation')}</p>}
              </div>
              <div className="flex items-center gap-3 pt-6">
                <input id="svc-published" type="checkbox" className="h-4 w-4 accent-primary" {...register('isPublished')} />
                <Label htmlFor="svc-published">Published</Label>
              </div>
            </div>

            {/* ── Request Form ─────────────────────────────────────────────── */}
            <div className="rounded-md border p-4 space-y-4">
              <div>
                <p className="text-sm font-medium">Request Form</p>
                <p className="text-xs text-muted-foreground">
                  {isBookablePricingType
                    ? 'This service will appear on the request form since it has a fixed pricing model.'
                    : 'Set Pricing Type to Flat, Hourly, Per Minute, or Per Headshot to make this bookable via the request form.'}
                </p>
              </div>

              {/* Setup Time (hourly only) */}
              {showSetupTime && (
                <div className="space-y-1">
                  <Label htmlFor="svc-setup-time">Setup Time (hours)</Label>
                  <Input
                    id="svc-setup-time"
                    type="number"
                    min={0}
                    step={0.5}
                    placeholder="0"
                    {...register('setupTime', { valueAsNumber: true })}
                  />
                  <p className="text-xs text-muted-foreground">Extra hours added to the hourly total (e.g. travel/setup).</p>
                </div>
              )}

              {/* Toggles */}
              <div className="grid grid-cols-2 gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-primary" {...register('isEvent')} />
                  Is Event (show date/time/location)
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-primary" {...register('showAudioOption')} />
                  Show Audio Option
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-primary" {...register('hasComplexity')} />
                  Has Complexity Tiers
                </label>
              </div>

              {/* Complexity fields */}
              {watchedHasComplexity && (
                <div className="space-y-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Complexity Pricing</p>
                  <div className="grid grid-cols-3 gap-3">
                    {(['Simple', 'Moderate', 'Complex'] as const).map((level) => {
                      const key = level.toLowerCase() as 'simple' | 'moderate' | 'complex'
                      return (
                        <div key={key} className="space-y-1">
                          <Label htmlFor={`complexity-price-${key}`}>{level} ($)</Label>
                          <Input
                            id={`complexity-price-${key}`}
                            type="number"
                            min={0}
                            step={0.01}
                            placeholder="0"
                            {...register(`complexity${level}Price` as const, { valueAsNumber: true })}
                          />
                        </div>
                      )
                    })}
                  </div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Complexity Descriptions</p>
                  <div className="space-y-2">
                    {(['Simple', 'Moderate', 'Complex'] as const).map((level) => (
                      <div key={level} className="space-y-1">
                        <Label htmlFor={`complexity-text-${level.toLowerCase()}`}>{level}</Label>
                        <Input
                          id={`complexity-text-${level.toLowerCase()}`}
                          placeholder={`Describe ${level.toLowerCase()} complexity…`}
                          {...register(`complexity${level}Text` as const)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Available Add-ons */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Available Add-ons</p>
                {addons.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No add-ons configured. Create one in the Add-ons section below.</p>
                ) : (
                  <div className="space-y-1.5">
                    <Controller
                      control={control}
                      name="addonIds"
                      render={({ field }) => (
                        <>
                          {addons.map((addon) => {
                            const checked = field.value.includes(addon.id)
                            return (
                              <label key={addon.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 accent-primary"
                                  checked={checked}
                                  onChange={(e) => field.onChange(
                                    e.target.checked
                                      ? [...field.value, addon.id]
                                      : field.value.filter((v) => v !== addon.id)
                                  )}
                                />
                                {addon.label}
                              </label>
                            )
                          })}
                        </>
                      )}
                    />
                  </div>
                )}
              </div>

              {/* Provider Positions — who gets notified when this service is requested */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Provider Positions</p>
                <p className="text-xs text-muted-foreground">
                  Members with any of these positions and Available for Hire enabled get an email when this service is requested.
                </p>
                {activePositions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No positions configured. Add some in Configuration.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-1.5">
                    <Controller
                      control={control}
                      name="providerPositionIds"
                      render={({ field }) => (
                        <>
                          {activePositions.map((p) => {
                            const checked = field.value.includes(p.id)
                            return (
                              <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 accent-primary"
                                  checked={checked}
                                  onChange={(e) => field.onChange(
                                    e.target.checked
                                      ? [...field.value, p.id]
                                      : field.value.filter((v) => v !== p.id)
                                  )}
                                />
                                {p.title}
                              </label>
                            )
                          })}
                        </>
                      )}
                    />
                  </div>
                )}
              </div>
            </div>

            {svcSubmitError && <p className="text-sm text-destructive">{svcSubmitError}</p>}
          </form>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSvcFormOpen(false)}>Cancel</Button>
            <Button type="submit" form="service-form" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : svcEditTarget ? 'Save Changes' : 'Create Service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Service photo sheet ──────────────────────────────────────────────── */}
      <ServicePhotoSheet
        open={svcPhotoSheetOpen}
        onClose={() => setSvcPhotoSheetOpen(false)}
        currentMediaId={watch('photoMediaId') || undefined}
        currentFocusX={watch('photoFocusX')}
        currentFocusY={watch('photoFocusY')}
        onAssign={(mediaId, mediaUrl, focusX, focusY) => {
          setValue('photoMediaId', mediaId)
          setValue('photoUrl', mediaUrl)
          setValue('photoFocusX', focusX)
          setValue('photoFocusY', focusY)
        }}
      />

      {/* ─── Service delete dialog ────────────────────────────────────────────── */}
      <Dialog open={svcDeleteOpen} onOpenChange={setSvcDeleteOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Service</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-foreground">{svcDeleteTarget?.title}</span>?{' '}
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSvcDeleteOpen(false); setSvcDeleteTarget(null) }}>Cancel</Button>
            <Button variant="destructive" onClick={() => void handleSvcDelete()} disabled={svcDeletingId !== null}>
              {svcDeletingId !== null ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Category form dialog ─────────────────────────────────────────────── */}
      <Dialog open={catFormOpen} onOpenChange={setCatFormOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{catEditTarget ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="cat-title">Title</Label>
              <Input
                id="cat-title"
                placeholder="e.g. Photography"
                value={catTitleInput}
                onChange={(e) => {
                  setCatTitleInput(e.target.value)
                  if (!catEditTarget) {
                    setCatKeyInput(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, ''))
                  }
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cat-key">Service Key</Label>
              <Input
                id="cat-key"
                placeholder="e.g. photography"
                value={catKeyInput}
                onChange={(e) => setCatKeyInput(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Machine key used to link services to this category on the request form. Auto-derived from title.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatFormOpen(false)}>Cancel</Button>
            <Button onClick={() => void handleCatSave()} disabled={!catTitleInput.trim() || catSaving}>
              {catSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Category delete dialog ───────────────────────────────────────────── */}
      <Dialog open={catDeleteOpen} onOpenChange={setCatDeleteOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Category</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-foreground">{catDeleteTarget?.title}</span>?{' '}
            Services using this category will not be affected.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCatDeleteOpen(false); setCatDeleteTarget(null) }}>Cancel</Button>
            <Button variant="destructive" onClick={() => void handleCatDelete()} disabled={catDeleting}>
              {catDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Addon form dialog ────────────────────────────────────────────────── */}
      <Dialog open={addonFormOpen} onOpenChange={setAddonFormOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{addonEditTarget ? 'Edit Add-on' : 'New Add-on'}</DialogTitle>
          </DialogHeader>

          <form
            id="addon-form"
            onSubmit={(e) => void handleAddonSubmit(onAddonSubmit)(e)}
            className="space-y-4 py-1"
          >
            {/* Label */}
            <div className="space-y-1">
              <Label htmlFor="addon-label">Label</Label>
              <Input id="addon-label" placeholder="e.g. Audio Recording" {...registerAddon('label')} />
              {addonErrors.label && <p className="text-sm text-destructive">{withErrorCode(String(addonErrors.label.message), 'app/validation')}</p>}
            </div>

            {/* Pricing Type */}
            <div className="space-y-1">
              <Label>Pricing Type</Label>
              <Controller
                control={addonControl}
                name="pricingType"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRICING_TYPES.map(({ value, label }) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Price fields */}
            {showAddonPriceField && (
              <div className={showAddonPriceMaxField ? 'grid grid-cols-2 gap-4' : undefined}>
                <div className="space-y-1">
                  <Label htmlFor="addon-price">
                    {watchedAddonPricingType === 'range' ? 'Min Price ($)' : 'Price ($)'}
                  </Label>
                  <Input
                    id="addon-price"
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder="0"
                    {...registerAddon('price', { valueAsNumber: true })}
                  />
                  {addonErrors.price && <p className="text-sm text-destructive">{withErrorCode(String(addonErrors.price.message), 'app/validation')}</p>}
                </div>
                {showAddonPriceMaxField && (
                  <div className="space-y-1">
                    <Label htmlFor="addon-price-max">Max Price ($)</Label>
                    <Input
                      id="addon-price-max"
                      type="number"
                      min={0}
                      step={0.01}
                      placeholder="0"
                      {...registerAddon('priceMax', { valueAsNumber: true })}
                    />
                    {addonErrors.priceMax && <p className="text-sm text-destructive">{withErrorCode(String(addonErrors.priceMax.message), 'app/validation')}</p>}
                  </div>
                )}
              </div>
            )}

            {/* Pricing Note */}
            <div className="space-y-1">
              <Label htmlFor="addon-pricing-note">
                Pricing Note <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input id="addon-pricing-note" placeholder="e.g. per session" {...registerAddon('pricingNote')} />
            </div>

            <Separator />
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Catalog / Request Form</p>

            {/* Selection Mode */}
            <div className="space-y-1">
              <Label>Selection Mode</Label>
              <Controller
                control={addonControl}
                name="selectionMode"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SELECTION_MODES.map(({ value, label }) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Pricing Mode */}
            <div className="space-y-1">
              <Label>Pricing Mode</Label>
              <Controller
                control={addonControl}
                name="pricingMode"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ADDON_PRICING_MODES.map(({ value, label }) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Rate + Max Qty */}
            <div className={showAddonMaxQty ? 'grid grid-cols-2 gap-4' : undefined}>
              <div className="space-y-1">
                <Label htmlFor="addon-rate">Rate ($)</Label>
                <Input
                  id="addon-rate"
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="0"
                  {...registerAddon('rate', { valueAsNumber: true })}
                />
              </div>
              {showAddonMaxQty && (
                <div className="space-y-1">
                  <Label htmlFor="addon-max-qty">Max Quantity</Label>
                  <Input
                    id="addon-max-qty"
                    type="number"
                    min={1}
                    step={1}
                    placeholder="1"
                    {...registerAddon('maxQty', { valueAsNumber: true })}
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* Order + Published */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="addon-order">Sort Order</Label>
                <Input id="addon-order" type="number" min={0} {...registerAddon('order', { valueAsNumber: true })} />
                {addonErrors.order && <p className="text-sm text-destructive">{withErrorCode(String(addonErrors.order.message), 'app/validation')}</p>}
              </div>
              <div className="flex items-center gap-3 pt-6">
                <input id="addon-published" type="checkbox" className="h-4 w-4 accent-primary" {...registerAddon('isPublished')} />
                <Label htmlFor="addon-published">Published</Label>
              </div>
            </div>

            {addonSubmitError && <p className="text-sm text-destructive">{addonSubmitError}</p>}
          </form>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddonFormOpen(false)}>Cancel</Button>
            <Button type="submit" form="addon-form" disabled={addonIsSubmitting}>
              {addonIsSubmitting ? 'Saving…' : addonEditTarget ? 'Save Changes' : 'Create Add-on'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Addon delete dialog ──────────────────────────────────────────────── */}
      <Dialog open={addonDeleteOpen} onOpenChange={setAddonDeleteOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Add-on</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-foreground">{addonDeleteTarget?.label}</span>?{' '}
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddonDeleteOpen(false); setAddonDeleteTarget(null) }}>Cancel</Button>
            <Button variant="destructive" onClick={() => void handleAddonDelete()} disabled={addonDeletingId !== null}>
              {addonDeletingId !== null ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
